import { z } from "zod";

/**
 * The available data sources for user verification, the order here
 * also dictates the order they appear in the UI
 */
export const VERIFICATION_DATA_SOURCES = {
  AUSTRALIAN_DRIVERS_LICENCE: "AustralianDriversLicence",
  AUSTRALIAN_PASSPORT: "AustralianPassport",
  MEDICARE_CARD: "MedicareCard",
  AUSTRALIAN_VISA: "AustralianVisaCheck",
  NEW_ZEALAND_DRIVERS_LICENCE: "NewZealandDriversLicence",
  NEW_ZEALAND_PASSPORT: "NewZealandPassport",
  NATIONAL_ID: "NationalId",
} as const;

/** The available data sources for user verification */
export type VerificationDataSource =
  (typeof VERIFICATION_DATA_SOURCES)[keyof typeof VERIFICATION_DATA_SOURCES];

export const USER_VERIFICATION_ATTEMPT_STATUSES = [
  "IN_PROGRESS",
  "UNVERIFIED",
  "VERIFIED",
  "VERIFIED_ADMIN",
  "PENDING_REVIEW",
  "PENDING_MANUAL_REVIEW",
  "ERROR_OCCURRED",
  "FAILED",
  "REJECTED_FOR_RESUBMIT",
] as const;

export type UserVerificationDataSourceAttemptStatus =
  (typeof USER_VERIFICATION_ATTEMPT_STATUSES)[number];

export const VERIFICATION_STATUSES = [
  "UNVERIFIED",
  "VERIFIED",
  "VERIFIED_ADMIN",
  "IN_PROGRESS",
  "PENDING_REVIEW",
  "ERROR_OCCURRED",
  "FAILED",
] as const;

export const USER_VERIFICATION_STATUSES = [
  "UNVERIFIED",
  "VERIFIED_ELECTRONIC", // NTRC
  "VERIFIED_DOCUMENT", // IoM
  "PARTIALLY_VERIFIED_ELECTRONIC", // IoM
  "NOT_REQUIRED",
] as const;

export const PHONE_VERIFIED_STATUSES = ["VERIFIED", "VERIFIED_ADMIN"];

export const USER_VERIFIED_STATUSES = [
  "VERIFIED_ELECTRONIC", // NTRC
  "VERIFIED_DOCUMENT", // IoM
  "PARTIALLY_VERIFIED_ELECTRONIC", // IoM
];

export const VERIFICATION_ATTEMPT_TYPES = {
  VIX_VERIFY: "VIXIdentityDocuments",
  SUMSUB: "SumsubIdentityDocuments",
} as const;

export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];
export type UserVerificationStatus =
  (typeof USER_VERIFICATION_STATUSES)[number];

const australianDriversLicenceAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.enum(VERIFICATION_ATTEMPT_TYPES),
  type: z.literal("AustralianDriversLicence"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    cardNumber: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    dateOfBirth: z.string(),
    state: z.string(),
    expiryDate: z.string(),
  }),
});

const newZealandDriversLicenceAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.VIX_VERIFY),
  type: z.literal("NewZealandDriversLicence"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    versionNumber: z.string(),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    dateOfBirth: z.string(),
    expiryDate: z.string(),
  }),
});

const australianPassportAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.VIX_VERIFY),
  type: z.literal("AustralianPassport"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    dateOfBirth: z.string(),
    expiryDate: z.string(),
  }),
});

const newZealandPassportAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.VIX_VERIFY),
  type: z.literal("NewZealandPassport"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    dateOfBirth: z.string(),
    expiryDate: z.string(),
  }),
});

const australianVisaCheckAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.VIX_VERIFY),
  type: z.literal("AustralianVisaCheck"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    dateOfBirth: z.string(),
    countryOfIssue: z.string(),
    expiryDate: z.string(),
  }),
});

const medicareCardAttemptSchema = z.object({
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.VIX_VERIFY),
  type: z.literal("MedicareCard"),
  terms: z.boolean(),
  fields: z.object({
    number: z.string(),
    dateOfBirth: z.string(),
    cardColour: z.string(),
    expiryDate: z.string(),
    individualReferenceNumber: z.string(),
    nameOnCard: z.string(),
    nameLine2: z.string().optional(),
    nameLine3: z.string().optional(),
    nameLine4: z.string().optional(),
  }),
});

export const submitVixVerificationAttemptPayloadSchema = z.discriminatedUnion(
  "type",
  [
    australianDriversLicenceAttemptSchema,
    newZealandDriversLicenceAttemptSchema,
    australianPassportAttemptSchema,
    newZealandPassportAttemptSchema,
    australianVisaCheckAttemptSchema,
    medicareCardAttemptSchema,
  ],
);

export const submitSumsubVerificationAttemptPayloadSchema = z.object({
  terms: z.boolean(),
  attemptId: z.string(),
  attemptType: z.literal(VERIFICATION_ATTEMPT_TYPES.SUMSUB),
  destination: z.string(),
});

export const submitVerificationAttemptPayloadSchema = z.discriminatedUnion(
  "attemptType",
  [
    submitVixVerificationAttemptPayloadSchema,
    submitSumsubVerificationAttemptPayloadSchema,
  ],
);

export type SubmitVerificationAttemptPayload = z.infer<
  typeof submitVerificationAttemptPayloadSchema
>;

const verificationPlanPathSchema = z.object({
  maxSourcesRequired: z.number().nullish(),
  requiredStatus: z.enum(USER_VERIFICATION_STATUSES),
  attemptId: z.string(),
  attemptType: z.enum(VERIFICATION_ATTEMPT_TYPES),
  attemptStatus: z.enum(USER_VERIFICATION_ATTEMPT_STATUSES),
});

export const verificationPlanResponseSchema = z.object({
  paths: z.array(verificationPlanPathSchema),
  currentStatus: z.enum(USER_VERIFICATION_STATUSES),
});
